<template>
  <div>
    <Loader
      v-show="loading"
      :loading="loading"
    />
    <v-card>
      <v-card-title>
        <v-row no-gutters>
          <span class="headline font-weight-medium">{{ header }}</span>
          <v-spacer />
          <v-btn
            v-if="!isNew"
            icon
            large
            @click="showAgreementForm"
          >
            <v-icon>{{ agreementFormShow === null ? 'mdi-format-vertical-align-bottom' : 'mdi-format-vertical-align-top' }}</v-icon>
          </v-btn>
          <IconBtn
            icons="close"
            :large="true"
            color="grey darken-4"
            @click="close"
          />
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <CustomTextField
              v-model="title"
              label="Назва"
              :hide-details="true"
              :required="true"
              :error-messages="$v.title.$invalid ? errors.TITLE : []"
              @input="isChanged = true"
            />
          </v-col>
          <v-col>
            <EmployeeSelect
              v-model="owner"
              :multiple-select="false"
              label="Власник"
              @input="isChanged = true"
            />
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="agreementFormShow"
          flat
        >
          <v-expansion-panel
            hide-actions
          >
            <v-expansion-panel-content>
              <v-row>
                <v-col>
                  <CompanySelect
                    v-model="company"
                    :multiple-select="false"
                    label="Компанія"
                    @input="isChanged = true"
                  />
                </v-col>
                <v-col>
                  <ContactsSelect
                    v-model="contacts"
                    :multiple-select="true"
                    label="Контакти"
                    @input="isChanged = true"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <DecimalEditor
                    v-model="amount"
                    label="Сума"
                    @input="isChanged = true"
                  />
                </v-col>
                <v-col>
                  <CurrencySelect
                    v-model="currency"
                    :multiple-select="false"
                    label="Валюта"
                    @input="isChanged = true"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <SalesFunnelTypeSelect
                    v-model="salesFunnelType"
                    :multiple-select="false"
                    label="Тип воронки"
                    @input="salesFunnelTypeChange"
                  />
                </v-col>
                <v-col>
                  <SalesFunnelStageSelect
                    v-model="salesFunnelStage"
                    :multiple-select="false"
                    label="Стадія воронки"
                    :disabled="salesFunnelType == null"
                    :sales-funnel-type-id="salesFunnelType != null ? salesFunnelType.id : null"
                    @input="isChanged = true"
                  />
                </v-col>
              </v-row>
              <Markdown
                :id="id"
                v-model="description"
                @input="isChanged = true"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <PrimaryBtn
          :loading="loading"
          :disabled="!isChanged || isFormInvalid"
          @click="save"
        />
        <PrimaryBtn
          v-if="!isNew"
          :loading="loading"
          text="Додати завдання"
          @click="addTask"
        />
      </v-card-actions>
    </v-card>
    <div
      v-if="taskFormShow && !isNew"
      class="task-component"
    >
      <Task
        :id="taskId"
        :is-in-agreement="true"
        :base-agreement="{id:id, title: title}"
        @taskAdded="taskAdded"
        @close="closeTaskForm"
      />
    </div>
    <v-card
      v-if="contacts.length > 0"
      class="mt-3"
    >
      <v-card-title>
        <v-row no-gutters>
          <span class="headline font-weight-medium">Контакти</span>
        </v-row>
      </v-card-title>
      <v-card-text>
        <template
          v-for="(contact, index) in contacts"
        >
          <div :key="contact.id">
            <v-row>
              <v-col style="color:black">
                {{ contact.title }} - {{ contact.position }}
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                Номери телефонів: {{ contact.phones.join(', ') }}
              </v-col>
              <v-col cols="12">
                Emails: {{ contact.emails.join(', ') }}
              </v-col>
            </v-row>
            <v-divider v-if="index < contacts.length - 1" />
          </div>
        </template>
      </v-card-text>
    </v-card>
    <template v-if="tasks.length > 0">
      <v-card
        v-for="task in tasks"
        :key="task.id"
        class="mt-3"
      >
        <v-card-text>
          <div class="text-h6 font-weight-medium">
            <v-row no-gutters>
              <v-col>
                Найменування: {{ task.title }}
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="1">
                Дата: {{ moment(task.date).format("DD.MM.YY") }}
              </v-col>
              <v-col>
                Виконавець: {{ task.implementer ? task.implementer.title : "" }}
              </v-col>
              <v-col>
                Тип: {{ task.type > 0 ? taskTypes.getEnumDescription(task.type) : "" }}
              </v-col>
              <v-col>
                Статус: {{ task.status > 0 ? taskStatuses.getEnumDescription(task.status) : "" }}
              </v-col>
              <v-col>
                Дата виконання: {{ moment(task.planDate).format("DD.MM.YY") }}
              </v-col>
            </v-row>
          </div>
          <v-divider class="py-2" />
          <div
            style="color:black"
            v-html="task.description"
          />
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import * as agreementsApi from "../../backend/agreementsApi.js";
import Loader from "../../../components/Loader.vue";
import IconBtn from "../../../components/IconBtn.vue";
import PrimaryBtn from "../../../components/PrimaryBtn.vue";
import CustomTextField from "../../../components/CustomTextField.vue";
import { ERRORS } from "@/helpers";
import { required } from "vuelidate/lib/validators";
import ContactsSelect from "../contacts/ContactsSelect.vue";
import EmployeeSelect from "../employees/EmployeesSelect.vue";
import CompanySelect from "../companies/CompanySelect.vue";
import CurrencySelect from "../currencies/CurrenciesSelect.vue";
import Markdown from "../../components/Markdown.vue";
import DecimalEditor from "../../../components/DecimalEditor.vue";
import SalesFunnelTypeSelect from "../salesFunnelTypes/SalesFunnelTypesSelect.vue";
import SalesFunnelStageSelect from "../salesFunnelStages/SalesFunnelStagesSelect.vue";
import Task from "../tasks/Task.vue";
import * as taskStatusEnum from "../../helpers/enums/TaskStatus.js";
import * as taskTypeEnum from "../../helpers/enums/TaskType.js";
import moment from "moment";
moment.locale("uk");

export default {
  components: {
    Loader,
    IconBtn,
    PrimaryBtn,
    CustomTextField,
    ContactsSelect,
    EmployeeSelect,
    CompanySelect,
    CurrencySelect,
    Markdown,
    DecimalEditor,
    SalesFunnelTypeSelect,
    SalesFunnelStageSelect,
    Task,
  },
  props: ["id"],
  data: () => ({
    loading: false,
    errorException: "",
    isChanged: false,
    title: "",
    description: "",
    amount: 0,
    owner: null,
    company: null,
    currency: null,
    contacts: [],
    salesFunnelType: null,
    salesFunnelStage: null,
    tasks: [],
    taskId: "0",
    agreementFormShow: 0,
    taskFormShow: true,
  }),
  validations: {
    title: {
      required,
    },
  },
  computed: {
    isFormInvalid(){
      return this.$v.title.$invalid;
    },
    isNew() {
      return this.id == 0;
    },
    header() {
      return this.isNew ? "Створення угоди" : "Редагування угоди";
    },
    errors() {
      return {
        TITLE: [ERRORS.REQUIRED],
      };
    },
    taskStatuses() {
      return taskStatusEnum;
    },
    taskTypes(){
      return taskTypeEnum;
    },
  },
  created() {
    if (!this.isNew) {
      this.agreementFormShow = null;
      this.taskFormShow = false;
      this.initData();
    }
  },
  methods: {
    async initData(){
      await agreementsApi.getAgreement(this.id).then((entity) => {
        this.title = entity.title;
        this.description = entity.description;
        this.amount = entity.amount;
        this.owner = entity.owner;
        this.company = entity.company;
        this.currency = entity.currency;
        this.contacts = entity.contacts;
        this.salesFunnelType = entity.salesFunnelType;
        this.salesFunnelStage = entity.salesFunnelStage;
        this.tasks = entity.tasks;
      });
    },
    addTask(){
      this.taskFormShow = true;
    },
    taskAdded(){
      this.closeTaskForm();
      this.initData();
    },
    closeTaskForm(){
      this.taskFormShow = false;
    },
    async save() {
      this.loading = true;
      var contactIds = this.contacts.map(e=>{
        return e.id;
      });

      var newEntity = {
        title: this.title,
        description: this.description,
        amount: this.amount,
        ownerId: this.owner?.id,
        companyId: this.company?.id,
        currencyId: this.currency?.id,
        salesFunnelTypeId: this.salesFunnelType?.id,
        salesFunnelStageId: this.salesFunnelStage?.id,
        contacts: contactIds,
      };
      if (this.isNew) {
        await agreementsApi
          .createAgreement(newEntity)
          .then(() => {
            this.$router.push({ path: "/crm-agreements" });
          });
      } else {
        await agreementsApi.updateAgreement(this.id, newEntity);
        this.initData();
        this.$notifySuccess();
      }
      this.loading = false;
    },
    close() {
      if (this.modal) {
        this.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
    salesFunnelTypeChange(){
      this.isChanged = true;
      this.salesFunnelStage = null;
    },
    showAgreementForm() {
      const value = this.agreementFormShow != null ? null : 0;
      this.agreementFormShow = value;
    },
  },
};
</script>

<style lang="css">
.v-expansion-panel-content .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
<style scoped>
.task-component{
  width: 70%;
  margin-top: 12px;
  margin-right: auto;
  margin-bottom: 0px;
  margin-left: auto;
}
</style>